import React, { useContext, useRef, useState } from 'react';
import { useSelector } from 'src/client/redux';
import { useJoinUrl } from 'src/client/components/buttons/JoinButton';
import { HEADER_DISCOUNTS } from 'src/client/constants/joinCampaigns';
import { HeaderContext } from '../HeaderProvider';
import {
  HeaderMenuItemRegister,
  HeaderMenuItemVisibilityChecker,
  useHeaderMenuItemVisibility,
} from './useHeaderMenuItemVisibility';

export type HeaderMenuController = {
  containerRef: React.RefObject<HTMLDivElement>;
  discountJoinUrl: string;
  hasUser: boolean;
  isAuthResolved: boolean;
  isVisibilityResolved: boolean;
  isVisible: HeaderMenuItemVisibilityChecker;
  mainHeaderRef: React.RefObject<HTMLDivElement>;
  registerItem: HeaderMenuItemRegister;
  searchBarOpen: boolean;
  searchToggleRef: React.RefObject<HTMLDivElement>;
  toggleNotificationsPanel: () => void;
  toggleRecentsBox: () => void;
  toggleSearchBar: () => void;
};

export const useHeaderBarController = () => {
  const { config } = useSelector((state) => state.app);
  const user = useSelector((state) => state.auth.user);
  const authLoaded = useSelector((state) => Boolean(state.auth.loaded));
  const { closeMobileMenu, mobileMenuOpened, setActiveNestedMobileMenu, setMobileMenuOpened } = useContext(
    HeaderContext,
  );

  const mainHeaderRef = useRef<HTMLDivElement>(null);
  const menuItemsContainerRef = useRef<HTMLDivElement>(null);
  const searchToggleRef = useRef<HTMLDivElement>(null);

  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [recentsOpen, setRecentsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [discountJoinUrl] = useJoinUrl({ campaign: HEADER_DISCOUNTS });
  const hasUser = Boolean(user);

  const eligibilityContext = {
    hasUser,
    userValidSubscription: Boolean(user?.validSubscription),
    configHasBlog: Boolean(config.hasBlog),
    configHasUpdates: Boolean(config.hasUpdates),
    configMoviesActive: Boolean(config.movies?.active),
    configMoviesRequiresAuth: Boolean(config.movies?.requiresAuth),
    enableSeoCategoriesMenu: Boolean(config.featureFlags?.enableSeoCategoriesMenu),
  };

  const { isVisible, isVisibilityResolved, registerItem } = useHeaderMenuItemVisibility({
    eligibilityContext,
    menuItemsContainerRef,
  });

  const closeNotificationsPanel = () => setNotificationsOpen(false);
  const closeRecentsBox = () => setRecentsOpen(false);
  const closeSearchBar = () => setSearchBarOpen(false);
  const handleMobileMenuScrimClick = () => closeMobileMenu();
  const toggleMobileMenu = () => setMobileMenuOpened((opened) => (opened && setActiveNestedMobileMenu(null), !opened));
  const toggleNotificationsPanel = () => setNotificationsOpen((open) => !open);
  const toggleRecentsBox = () => setRecentsOpen((open) => !open);
  const toggleSearchBar = () => setSearchBarOpen((open) => !open);

  const headerMenuController: HeaderMenuController = {
    containerRef: menuItemsContainerRef,
    discountJoinUrl,
    hasUser,
    isAuthResolved: authLoaded,
    isVisibilityResolved,
    isVisible,
    mainHeaderRef,
    registerItem,
    searchBarOpen,
    searchToggleRef,
    toggleNotificationsPanel,
    toggleRecentsBox,
    toggleSearchBar,
  };

  return {
    closeNotificationsPanel,
    closeRecentsBox,
    closeSearchBar,
    handleMobileMenuScrimClick,
    headerMenuController,
    mainHeaderRef,
    mobileMenuOpened,
    notificationsOpen,
    recentsOpen,
    searchBarOpen,
    searchToggleRef,
    toggleMobileMenu,
  };
};
